// frontend/src/utils/dosingUrlCodec.ts
import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from 'lz-string';
import { z } from 'zod';
import type { PublicDosingBatch } from '@/types/publicDosing';

// ============================================
// CONSTANTS
// ============================================

export const CURRENT_VERSION = 1;
export const MAX_ENCODED_LENGTH = 16_000; // ~16KB limit for URL safety

// ============================================
// ZOD SCHEMAS FOR VALIDATION
// ============================================

// Sanitization helpers - strip anything that could be XSS
const sanitizeName = (s: string) => s.replace(/[<>]/g, '').replace(/[^\x20-\x7E]/g, '').slice(0, 100);
const sanitizeElementName = (s: string) => s.replace(/[^a-zA-Z0-9\s-]/g, '').slice(0, 50);

const MinimalElementSchema = z.object({
  n: z.string().transform(sanitizeElementName),
  m: z.number().min(0).max(10000),
});

const MinimalContainerSchema = z.object({
  n: z.string().transform(sanitizeName),
  m: z.number().min(0).max(100000),
  e: z.array(MinimalElementSchema).max(50),
});

// FIX C3: Add version field for future compatibility
const MinimalBatchSchema = z.object({
  v: z.literal(CURRENT_VERSION), // Version must match
  n: z.string().transform(sanitizeName),
  d: z.number().int().min(1).max(365),
  c: z.array(MinimalContainerSchema).max(20),
});

type MinimalBatch = z.infer<typeof MinimalBatchSchema>;

// ============================================
// CONVERSION FUNCTIONS
// ============================================

function toMinimal(batch: PublicDosingBatch): MinimalBatch {
  return {
    v: CURRENT_VERSION, // FIX C3: Include version
    n: batch.name,
    d: batch.days,
    c: batch.containers.map((c) => ({
      n: c.name,
      m: c.ml_per_day,
      e: c.elements.map((e) => ({
        n: e.element_name,
        m: e.ml_per_day,
      })),
    })),
  };
}

function fromMinimal(minimal: MinimalBatch): PublicDosingBatch {
  return {
    name: minimal.n,
    days: minimal.d,
    containers: minimal.c.map((c) => ({
      name: c.n,
      ml_per_day: c.m,
      elements: c.e.map((e) => ({
        element_name: e.n,
        ml_per_day: e.m,
      })),
    })),
  };
}

// ============================================
// URL ENCODING/DECODING (Pure Functions)
// ============================================

export function encodeBatchToUrl(batch: PublicDosingBatch): string {
  const minimal = toMinimal(batch);
  const json = JSON.stringify(minimal);
  return compressToEncodedURIComponent(json);
}

/**
 * Decodes and VALIDATES URL-encoded batch data.
 * Returns null if invalid or malformed - never throws.
 */
export function decodeBatchFromUrl(encoded: string): PublicDosingBatch | null {
  if (!encoded) return null;

  // FIX B4: Check max length before processing
  if (encoded.length > MAX_ENCODED_LENGTH) {
    console.warn('Encoded batch exceeds max length, rejecting');
    return null;
  }

  try {
    const json = decompressFromEncodedURIComponent(encoded);
    if (!json) return null;

    // Parse JSON safely
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      return null;
    }

    // Validate against schema - this sanitizes AND validates
    // FIX C3: Version mismatch will cause validation failure
    const result = MinimalBatchSchema.safeParse(parsed);
    if (!result.success) {
      console.warn('Invalid batch data in URL:', result.error.issues);
      return null;
    }

    return fromMinimal(result.data);
  } catch (err) {
    console.warn('Failed to decode batch from URL:', err);
    return null;
  }
}
